/**
 * Common-goal protocol recommendations.
 *
 * Ranking is catalog-score-first (no brand favors). For each of the three
 * homepage goals, pick the highest-scored matching products in all five
 * Vyvata categories. Same rules as the listicles: highest Vyvata score
 * first, and one SKU per brand (drop size / flavor / pack / variant
 * siblings). Duplicate listings also collapse via VSFCompare identity keys.
 */
(function (root) {
  const CATEGORIES = [
    { key: 'supplements', name: 'Supplements' },
    { key: 'wearables_tracking', name: 'Wearables & Tracking' },
    { key: 'diagnostics_testing', name: 'Diagnostics & Testing' },
    { key: 'sleep_optimization', name: 'Sleep Optimization' },
    { key: 'recovery_regeneration', name: 'Recovery & Regeneration' },
  ];

  const MIN_SCORE = 55;
  const TIER_RANK = { elite: 4, verified: 3, standard: 2, provisional: 1, rejected: 0 };

  const PROTOCOLS = [
    {
      key: 'cognitive-performance',
      title: 'Cognitive Performance',
      tags: 'Focus · Memory · Clarity',
      summary:
        'Highest-ranked catalog products for focus, memory, and mental clarity. Cards run highest Vyvata score first, one SKU per brand. IgniCognition is featured as the matching Igniton cognition SKU. Supplement picks also follow the creatine, lion’s mane, L-theanine, and vitamin D3 stack cited on the homepage. Wearables, diagnostics, sleep, and recovery fill in from the same ranking.',
      icon: 'brain',
      excludeFamilies: [],
      categories: {
        supplements: {
          subcategories: ['cognitive_nootropics'],
          families: ['creatine', 'lions_mane', 'l_theanine', 'vitamin_d', 'rhodiola'],
          goalTags: ['cognitive', 'focus', 'memory', 'brain', 'clarity', 'nootropic'],
          tokens: [
            'creatine',
            "lion's mane",
            'lions mane',
            'hericium',
            'theanine',
            'vitamin d',
            'vitamin d3',
            'nootropic',
            'bacopa',
            'alpha-gpc',
            'phosphatidylserine',
          ],
          stackFamilies: ['creatine', 'lions_mane', 'l_theanine', 'vitamin_d'],
          featuredHandles: ['ignicognition™', 'ignicognition'],
          limit: 8,
        },
        wearables_tracking: {
          subcategories: ['hearables'],
          families: [],
          goalTags: ['cognitive', 'focus', 'brain'],
          tokens: ['muse', 'eeg', 'neuro', 'focus', 'brain'],
          stackFamilies: [],
          limit: 4,
        },
        diagnostics_testing: {
          subcategories: ['nutrient_deficiency_testing', 'genetic_dna', 'blood_panel_metabolic'],
          families: ['vitamin_d', 'omega_3', 'vitamin_b12'],
          goalTags: ['cognitive', 'brain'],
          tokens: ['omega-3 index', 'vitamin d', 'apoe', 'b12', 'homocysteine', 'cognitive'],
          stackFamilies: [],
          limit: 4,
        },
        sleep_optimization: {
          subcategories: ['neurostim_sleep_headbands', 'sleep_tracking_wearables', 'sleep_environment'],
          families: [],
          goalTags: ['cognitive', 'focus', 'clarity'],
          tokens: ['muse', 'eeg', 'slow-wave', 'deep sleep'],
          stackFamilies: [],
          limit: 4,
        },
        recovery_regeneration: {
          subcategories: ['red_light_photobiomodulation', 'hrv_recovery_apps'],
          families: [],
          goalTags: ['cognitive', 'brain'],
          tokens: ['red light', 'near-infrared', 'hrv', 'photobiomodulation'],
          stackFamilies: [],
          limit: 4,
        },
      },
    },
    {
      key: 'sleep-recovery',
      title: 'Sleep & Recovery',
      tags: 'Deep Sleep · Recovery · HRV',
      summary:
        'Highest-ranked products for deep sleep, overnight recovery, and HRV. Cards run highest Vyvata score first, one SKU per brand. The supplement layer prefers magnesium glycinate, glycine, and tart cherry and skips melatonin-first formulas. Sleep hardware, diagnostics, wearables, and recovery tools use the same ranking.',
      icon: 'moon',
      excludeFamilies: ['melatonin'],
      categories: {
        supplements: {
          subcategories: ['stress_mood_mental'],
          families: ['magnesium', 'l_theanine'],
          goalTags: ['sleep', 'relaxation'],
          tokens: ['glycine', 'tart cherry', 'montmorency', 'magnesium glycinate', 'magnesium bisglycinate', 'theanine'],
          stackFamilies: ['magnesium', 'l_theanine'],
          limit: 8,
        },
        wearables_tracking: {
          subcategories: ['smart_rings', 'spo2_respiratory'],
          families: [],
          goalTags: ['sleep', 'hrv'],
          tokens: ['oura', 'whoop', 'sleep', 'hrv', 'readiness'],
          stackFamilies: [],
          limit: 4,
        },
        diagnostics_testing: {
          subcategories: ['nutrient_deficiency_testing'],
          families: ['magnesium', 'vitamin_d'],
          goalTags: ['sleep', 'cortisol'],
          tokens: ['cortisol', 'vitamin d', 'magnesium', 'sleep', 'melatonin'],
          stackFamilies: [],
          limit: 4,
        },
        sleep_optimization: {
          subcategories: [
            'sleep_supplements_nutraceuticals',
            'sleep_tracking_wearables',
            'sleep_environment',
            'white_noise_sound',
            'noncontact_sleep_monitors',
            'smart_mattresses',
            'neurostim_sleep_headbands',
          ],
          families: ['magnesium', 'l_theanine'],
          goalTags: ['sleep', 'recovery', 'deep sleep'],
          tokens: [
            'magnesium glycinate',
            'magnesium bisglycinate',
            'glycine',
            'tart cherry',
            'apigenin',
            'sleep',
          ],
          stackFamilies: ['magnesium'],
          limit: 8,
        },
        recovery_regeneration: {
          subcategories: ['hrv_recovery_apps', 'red_light_photobiomodulation', 'compression_therapy', 'cold_therapy'],
          families: [],
          goalTags: ['recovery', 'sleep', 'hrv', 'inflammation'],
          tokens: ['hrv', 'recovery', 'red light', 'compression', 'cold'],
          stackFamilies: [],
          limit: 4,
        },
      },
    },
    {
      key: 'inflammation-longevity',
      title: 'Inflammation & Longevity',
      tags: 'Inflammation · Oxidative Stress',
      summary:
        'Highest-ranked products for inflammation, oxidative stress, and cellular longevity. Cards run highest Vyvata score first, one SKU per brand. Supplement picks follow the omega-3, curcumin, NAC, and vitamin D stack cited on the homepage, then other high-scoring matches. Diagnostics, wearables, sleep, and recovery use the same ranking.',
      icon: 'pill',
      excludeFamilies: [],
      categories: {
        supplements: {
          subcategories: ['longevity_cellular', 'immune_antioxidants', 'cardiovascular_heart', 'joint_bone_connective'],
          families: ['omega_3', 'turmeric', 'nac', 'vitamin_d', 'nmn', 'nr', 'resveratrol'],
          goalTags: ['inflammation', 'longevity', 'antioxidant', 'joint', 'heart'],
          tokens: [
            'omega-3',
            'omega 3',
            'fish oil',
            'curcumin',
            'curcuminoid',
            'turmeric',
            'nac',
            'n-acetyl',
            'vitamin d',
            'nmn',
            'resveratrol',
          ],
          stackFamilies: ['omega_3', 'turmeric', 'nac', 'vitamin_d'],
          limit: 8,
        },
        wearables_tracking: {
          subcategories: ['cgm', 'smart_rings', 'smartwatches', 'fitness_activity_trackers', 'cardiac_ecg'],
          families: [],
          goalTags: ['inflammation', 'longevity', 'metabolic', 'hrv'],
          tokens: ['cgm', 'glucose', 'hrv', 'oura', 'whoop'],
          stackFamilies: [],
          limit: 4,
        },
        diagnostics_testing: {
          subcategories: ['blood_panel_metabolic', 'nutrient_deficiency_testing', 'genetic_dna', 'cancer_chronic_screening'],
          families: ['omega_3', 'vitamin_d'],
          goalTags: ['inflammation', 'longevity', 'aging'],
          tokens: [
            'crp',
            'hs-crp',
            'omega-3 index',
            'truage',
            'biological age',
            'epigenetic',
            'homocysteine',
            'longevity',
          ],
          stackFamilies: [],
          limit: 4,
        },
        sleep_optimization: {
          subcategories: ['sleep_tracking_wearables', 'sleep_environment', 'sleep_supplements_nutraceuticals'],
          families: ['magnesium', 'vitamin_d'],
          goalTags: ['inflammation', 'longevity', 'recovery'],
          tokens: ['sleep', 'circadian', 'circadian'],
          stackFamilies: [],
          limit: 4,
        },
        recovery_regeneration: {
          subcategories: [
            'red_light_photobiomodulation',
            'cold_therapy',
            'sauna_heat_therapy',
            'contrast_therapy',
            'compression_therapy',
          ],
          families: [],
          goalTags: ['inflammation', 'longevity', 'recovery'],
          tokens: ['red light', 'cold plunge', 'ice bath', 'sauna', 'infrared', 'inflammation'],
          stackFamilies: [],
          limit: 4,
        },
      },
    },
  ];

  function asList(value) {
    if (typeof root.VSFCompare === 'object' && root.VSFCompare.asList) {
      return root.VSFCompare.asList(value);
    }
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (value == null || value === '') return [];
    return [String(value)];
  }

  function escapeRe(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function hasToken(text, token) {
    const a = String(token || '').toLowerCase().trim();
    if (!a) return false;
    const re = new RegExp('(^|[^a-z0-9])' + escapeRe(a) + '(?=[^a-z0-9]|$)', 'i');
    return re.test(String(text || ''));
  }

  function productText(p) {
    if (root.VSFCompare && typeof root.VSFCompare.productText === 'function') {
      return root.VSFCompare.productText(p);
    }
    return [p.title, p.handle, (p.tokens || []).join(' ')].join(' ').toLowerCase();
  }

  const SOFT_FAMILY_ALIASES = [
    { key: 'omega_3', tokens: ['omega', 'epa', 'dha', 'fish oil', 'krill'] },
    { key: 'nac', tokens: ['nac', 'acetylcysteine', 'acetyl cysteine'] },
    { key: 'turmeric', tokens: ['turmeric', 'curcumin'] },
    { key: 'vitamin_d', tokens: ['vitamin d', 'vitamin d3', 'cholecalciferol'] },
    { key: 'magnesium', tokens: ['magnesium'] },
    { key: 'creatine', tokens: ['creatine'] },
    { key: 'l_theanine', tokens: ['theanine'] },
    { key: 'lions_mane', tokens: ["lion's mane", 'lions mane', 'hericium'] },
  ];

  function familyKeys(p) {
    const keys = [];
    if (root.VSFCompare && typeof root.VSFCompare.familiesOf === 'function') {
      root.VSFCompare.familiesOf(p).forEach((f) => keys.push(f.key));
    }
    const text = productText(p);
    SOFT_FAMILY_ALIASES.forEach((fam) => {
      if (keys.indexOf(fam.key) >= 0) return;
      const hit = (fam.tokens || []).some((t) => {
        if (fam.key === 'omega_3' && t === 'omega') return /omega/.test(text);
        return hasToken(text, t);
      });
      if (hit) keys.push(fam.key);
    });
    return keys;
  }

  function tierRank(p) {
    const t = String(p.tier || '').toLowerCase();
    return TIER_RANK[t] != null ? TIER_RANK[t] : 0;
  }

  function byScoreThenTier(a, b) {
    const ds = (b.score || 0) - (a.score || 0);
    if (ds) return ds;
    const dt = tierRank(b) - tierRank(a);
    if (dt) return dt;
    return String(a.title || '').localeCompare(String(b.title || ''));
  }

  const RESELLERS = new Set([
    'medsitis',
    'paywut',
    'nutritionadvice',
    'zoppinh',
    'o body + wellness',
    'o body wellness',
    'the digital health store',
    'your health sanctuary',
    'farmacias savia',
    'nyallergy',
  ]);

  const BRAND_ALIASES = [
    ['withings', 'Withings'],
    ['scanwatch', 'Withings'],
    ['airdoctor', 'AirDoctor'],
    ['air doctor', 'AirDoctor'],
    ['defendershield', 'DefenderShield'],
    ['defender shield', 'DefenderShield'],
    ['trudiagnostic', 'TruDiagnostic'],
    ['truage', 'TruDiagnostic'],
    ['truhealth', 'TruDiagnostic'],
    ['fullglow', 'FullGlow'],
    ['nutrabio', 'NutraBio'],
    ['pure encapsulations', 'Pure Encapsulations'],
    ['igniton', 'Igniton'],
    ['ignicognition', 'Igniton'],
    ['ignilongevity', 'Igniton'],
    ['ignirem', 'Igniton'],
    ['ignipeptide', 'Igniton'],
    ['game ready', 'Game Ready'],
    ['healthlight', 'HealthLight'],
    ['general pain relief', 'HealthLight'],
    ['muse 2', 'Muse'],
    ['nordic naturals', 'Nordic Naturals'],
    ['now foods', 'NOW Foods'],
    ['thorne', 'Thorne'],
    ['momentous', 'Momentous'],
    ['solgar', 'Solgar'],
    ['lumara', 'Lumara'],
    ['bearaby', 'Bearaby'],
    ['gravity blanket', 'Gravity Blankets'],
    ['fire cold plunge', 'Fire Cold Plunge'],
    ['fitbit', 'Fitbit'],
    ['dexcom', 'Dexcom'],
    ['garmin', 'Garmin'],
    ['omron', 'Omron'],
    ['ice barrel', 'Ice Barrel'],
    ['bodybio', 'BodyBio'],
    ['hodgins', 'Hodgins Harvest'],
    ['gaia herbs', 'Gaia Herbs'],
    ['hello health', 'Hello Health'],
    ['pts diagnostics', 'PTS Diagnostics'],
    ['cardiochek', 'PTS Diagnostics'],
    ['hemosure', 'Hemosure'],
    ['bio compression', 'Bio Compression'],
    ['oura', 'Oura'],
    ['whoop', 'WHOOP'],
  ];

  function protocolBrandKey(p) {
    const title = String((p && p.title) || '');
    const vendor = String((p && p.vendor) || '').trim();
    const handle = String((p && p.handle) || '');
    const hay = (title + ' ' + vendor + ' ' + handle).toLowerCase();
    for (let i = 0; i < BRAND_ALIASES.length; i += 1) {
      if (hay.indexOf(BRAND_ALIASES[i][0]) >= 0) return BRAND_ALIASES[i][1];
    }
    const vendorKey = vendor
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9+]+/g, ' ')
      .trim();
    if (vendorKey && !RESELLERS.has(vendorKey) && !/allergy/.test(vendorKey)) {
      return vendor;
    }
    const m = title.match(/^([A-Z][A-Za-z0-9&.'+™®]*(?:\s+[A-Z][A-Za-z0-9&.'+™®]*)?)/);
    if (m) return m[1].replace(/[™®]/g, '').trim();
    return handle || title.slice(0, 24);
  }

  function protocolByKey(key) {
    const needle = String(key || '').toLowerCase().replace(/^#/, '');
    return PROTOCOLS.find((p) => p.key === needle) || null;
  }

  function categoryName(key) {
    const tax = root.VSF_TAXONOMY;
    if (tax && Array.isArray(tax.categories)) {
      const hit = tax.categories.find((c) => c.key === key);
      if (hit && hit.name) return hit.name;
    }
    const fallback = CATEGORIES.find((c) => c.key === key);
    return fallback ? fallback.name : key;
  }

  function isExcluded(protocol, p) {
    const fams = familyKeys(p);
    return (protocol.excludeFamilies || []).some((k) => fams.indexOf(k) >= 0);
  }

  function matchesRule(p, rule) {
    if (!rule) return false;
    const subs = asList(p.vsf_subcategory);
    const goals = asList(p.goal_tags).map((g) => String(g).toLowerCase());
    const text = productText(p);
    const fams = familyKeys(p);

    if ((rule.subcategories || []).some((k) => subs.indexOf(k) >= 0)) return true;
    if ((rule.families || []).some((k) => fams.indexOf(k) >= 0)) return true;
    if ((rule.goalTags || []).some((g) => goals.indexOf(String(g).toLowerCase()) >= 0)) return true;
    if ((rule.tokens || []).some((t) => hasToken(text, t))) return true;
    return false;
  }

  function takeUnique(compare, seen, list) {
    if (compare && typeof compare.identityKeys === 'function') {
      const out = [];
      list.forEach((p) => {
        const keys = compare.identityKeys(p).filter(Boolean);
        if (keys.some((k) => seen.has(k))) return;
        keys.forEach((k) => seen.add(k));
        out.push(p);
      });
      return out;
    }
    const out = [];
    list.forEach((p) => {
      const k = 'h:' + String(p.handle || '').toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push(p);
    });
    return out;
  }

  function eligibleInCategory(products, catKey, protocol) {
    return (products || []).filter((p) => {
      if ((p.vsf_category || '') !== catKey) return false;
      if ((p.score || 0) < MIN_SCORE) return false;
      if (String(p.tier || '').toLowerCase() === 'rejected') return false;
      if (isExcluded(protocol, p)) return false;
      return true;
    });
  }

  function pickForCategory(products, protocol, catKey, compare) {
    const rule = (protocol.categories && protocol.categories[catKey]) || {};
    const limit = Math.max(1, Number(rule.limit) || 4);
    const pool = eligibleInCategory(products, catKey, protocol).slice().sort(byScoreThenTier);
    const matched = pool.filter((p) => matchesRule(p, rule));
    const stackFamilies = rule.stackFamilies || [];
    const featuredHandles = (rule.featuredHandles || []).map((h) =>
      String(h || '').toLowerCase().replace(/[™®]/g, '')
    );
    const chosen = [];

    function hasFamily(p, famKey) {
      return familyKeys(p).indexOf(famKey) >= 0;
    }
    function handleKey(p) {
      return String((p && p.handle) || '').toLowerCase().replace(/[™®]/g, '');
    }
    function canTake(p, list, opts) {
      if (!p) return false;
      const ignoreFamily = !!(opts && opts.ignoreFamily);
      const brand = protocolBrandKey(p);
      if (brand && list.some((x) => protocolBrandKey(x) === brand)) return false;
      const preview = takeUnique(compare, new Set(), list.concat([p]));
      if (preview.length !== list.length + 1) return false;
      if (!ignoreFamily) {
        const have = new Set();
        list.forEach((x) => familyKeys(x).forEach((k) => have.add(k)));
        if (familyKeys(p).some((k) => have.has(k))) return false;
      }
      return true;
    }
    function isProtected(p, list) {
      if (featuredHandles.indexOf(handleKey(p)) >= 0) return true;
      const rest = list.filter((x) => x !== p);
      return stackFamilies.some((k) => hasFamily(p, k) && !rest.some((x) => hasFamily(x, k)));
    }
    function placeRequired(hit, ignoreFamily) {
      if (!hit) return;
      if (chosen.some((p) => handleKey(p) === handleKey(hit))) return;
      if (chosen.length < limit && canTake(hit, chosen, { ignoreFamily: ignoreFamily })) {
        chosen.push(hit);
        return;
      }
      let replaceAt = -1;
      for (let i = chosen.length - 1; i >= 0; i -= 1) {
        const rest = chosen.filter((_, j) => j !== i);
        if (isProtected(chosen[i], chosen)) continue;
        if (canTake(hit, rest, { ignoreFamily: ignoreFamily })) {
          replaceAt = i;
          break;
        }
      }
      if (replaceAt >= 0) chosen.splice(replaceAt, 1, hit);
    }
    function bestForFamily(famKey, list) {
      return matched.find((p) => hasFamily(p, famKey) && canTake(p, list))
        || pool.find((p) => hasFamily(p, famKey) && canTake(p, list))
        || null;
    }
    function findFeatured(needle) {
      return pool.find((p) => handleKey(p) === needle)
        || pool.find((p) => handleKey(p).indexOf(needle) >= 0)
        || null;
    }

    matched.forEach((p) => {
      if (chosen.length >= limit) return;
      if (canTake(p, chosen)) chosen.push(p);
    });

    featuredHandles.forEach((needle) => {
      placeRequired(findFeatured(needle), true);
    });

    stackFamilies.forEach((famKey) => {
      if (chosen.some((p) => hasFamily(p, famKey))) return;
      placeRequired(bestForFamily(famKey, chosen), false);
    });

    if (chosen.length === 0) {
      pool.forEach((p) => {
        if (chosen.length >= limit) return;
        if (canTake(p, chosen)) chosen.push(p);
      });
    }

    return chosen.slice().sort(byScoreThenTier).slice(0, limit);
  }

  function recommend(products, protocolKey) {
    const protocol = protocolByKey(protocolKey);
    if (!protocol) return null;
    const compare = root.VSFCompare;
    const list = Array.isArray(products) ? products : [];
    const sections = CATEGORIES.map((cat) => {
      const items = pickForCategory(list, protocol, cat.key, compare);
      return {
        key: cat.key,
        name: categoryName(cat.key),
        items,
      };
    });
    return {
      protocol,
      sections,
    };
  }

  const api = {
    CATEGORIES,
    PROTOCOLS,
    MIN_SCORE,
    protocolByKey,
    protocolBrandKey,
    categoryName,
    matchesRule,
    recommend,
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.VSFProtocols = api;
})(typeof window !== 'undefined' ? window : globalThis);
